import { classes, events } from '@theme/constants';
import { parseHtml } from '@theme/dom';
import { publish } from '@theme/event-bus';
import { sectionStatusHandler, text } from '@theme/fetch';
import { debounce, updateQueryParam } from '@theme/helpers';
import { HTMLShopifySectionElement } from '@theme/html-custom-element';

customElements.define(
  'main-product',
  class MainProduct extends HTMLShopifySectionElement {
    requiredAttributes = ['section-id'];

    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.on('option-change', this.refs.form, this.handleOptionChange.bind(this));
      this.on('scroll', window, debounce(this.handleWindowScroll.bind(this)));

      if (this.refs.sticky) {
        new ResizeObserver(() => {
          this.storeStickyProductHeight();
        }).observe(this.refs.sticky);
      }

      /**
       * Included products slider.
       */
      if (this.refs.includedProductsProduct && this.refs.includedProductsSlider) {
        this.on('mouseenter', this.refs.includedProductsProduct.all, (event) => {
          this.refs.includedProductsSlider.swiper.slideToLoop(Number(event.target.dataset.index0));
        });

        this.on('swiperslidechangetransitionstart', this.refs.includedProductsSlider, ({ detail: [swiper] }) => {
          for (const product of this.refs.includedProductsProduct.all) {
            product.classList.toggle(classes.active, Number(product.dataset.index0) === swiper.realIndex);
          }
        });
      }
    }

    /**
     * Stores the height of the sticky product.
     * @param {string} height
     */
    storeStickyProductHeight(height) {
      document.body.style.setProperty(
        '--sticky-product-height',
        height ? height : `${this.refs.sticky.getBoundingClientRect().height.toFixed(2)}px`,
      );
    }

    /**
     * Handles the product form option change event.
     * @param {object} event - The event payload.
     */
    async handleOptionChange(event) {
      const response = await this.getSectionByOptionValues(event.detail.values, event.detail.selling_plan);
      const section = parseHtml(response, this.tagName.toLowerCase());

      publish(events.sectionUpdate, {
        sections: {
          [this.getAttribute('section-id')]: response,
        },
      });

      section &&
        updateQueryParam(
          'variant',
          section.getAttribute('selected-variant') === section.getAttribute('default-variant')
            ? null
            : section.getAttribute('selected-variant'),
        );
    }

    /**
     * Fetches and returns the updated section.
     * @param {string[]} values - The selected option values.
     * @param {string} [selling_plan] - The selling plan.
     * @returns {Promise<string>}
     */
    getSectionByOptionValues(values, selling_plan) {
      return text(
        fetch(
          `${window.location.pathname}?section_id=${this.getAttribute('section-id')}&option_values=${values.join(',')}${
            selling_plan ? `&selling_plan=${selling_plan}` : ''
          }`,
        ),
        sectionStatusHandler,
      );
    }

    /**
     * Handles the window scroll event.
     */
    handleWindowScroll() {
      if (!this.refs.submit || !this.refs.sticky) {
        return;
      }

      const { bottom: submitBottom } = this.refs.submit.getBoundingClientRect();
      const { bottom: mainBottom } = document.querySelector('.content-for-layout').getBoundingClientRect();
      const submitIsAboveViewport =
        submitBottom < (parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) ?? 0);
      const mainIsPastViewportBottom = mainBottom < window.innerHeight;

      this.refs.sticky.classList.toggle(classes.active, submitIsAboveViewport && !mainIsPastViewportBottom);

      if (submitIsAboveViewport && !mainIsPastViewportBottom) {
        this.storeStickyProductHeight();
      } else {
        this.storeStickyProductHeight('0px');
      }
    }

    onBlockSelect(event) {
      [this.refs.productDrawer, this.refs.detailsDrawer].forEach((drawer) => {
        if (drawer.contains(event.target)) {
          drawer.open();
        }
      });
    }

    onBlockDeselect(event) {
      [this.refs.productDrawer, this.refs.detailsDrawer].forEach((drawer) => {
        if (drawer === event.target) {
          drawer.close();
        }
      });
    }
  },
);
